import * as api from 'api';

import ModalWindow from '../ModalWindow/ModalWindow';
import { isUrlWithProtocol } from '../ReactComponents/Helpers/validateUrl';
import Tools from '../Tools/Tools';

const WEBS_EXCLUDED_URLS_MAX_LENGTH = 10;
const SINGLE_WEB_EXCLUDED_URLS_MAX_LENGTH = 500;

class CompetitionFilterModal {
	/**
	 * @param {{id: number, name: string}[]} selectedWebs
	 */
	constructor(selectedWebs) {
		this.selectedWebs = selectedWebs;
		this.show();
		this.hasBeenValidated = false;
	}

	show() {
		if (!this._modalWrap) {
			this._createModal();
		}

		this._modal.openByCustomEvent(this._modalWrap);
	}

	/**
	 * Returns elements displayed in modal.
	 * @return {Element[]}
	 */
	_createContent() {
		const title = Tools.createElement('h2', {
			innerText: 'Vyfiltrujte konkurenci',
		});
		const label = Tools.createElement('label', {
			for: 'excluded_urls_string',
			// eslint-disable-next-line typescript-eslint/restrict-template-expressions
			innerText: `Filtr konkurence (maximalne ${WEBS_EXCLUDED_URLS_MAX_LENGTH} url)`,
		});

		const textArea = Tools.createElement('textarea', {
			name: 'excludedUrlsString',
		});

		const errorMessage = Tools.createElement('p', {
			class: 'Error Error--input hidden',
		});

		const buttonsWrapper = Tools.createElement('div', {
			class: 'Modal-buttons',
		});

		const saveButton = Tools.createElement('button', {
			class: 'Modal-submit',
			innerText: 'Uložit',
		});

		textArea.addEventListener('blur', (event) =>
			this._handleTextAreaBlur(event.target.value, errorMessage, saveButton),
		);

		textArea.addEventListener('input', (event) => {
			Tools.debounce(this._handleTextAreaChange.bind(this, event.target.value, errorMessage, saveButton), 300, false)();
		});

		// while waiting for response from server we render spinner
		const loadingModalOverlay = Tools.createElement('div', {
			class: 'Modal-loading-overlay hidden',
			innerHTML: "<img class='Form-status-spinner' src='/app/static/img/loading.gif' alt=\"loader\" />",
		});

		saveButton.addEventListener('click', () =>
			this._handleSave(
				this.selectedWebs.map(({ id }) => id),
				loadingModalOverlay,
			),
		);

		Tools.appendChilds(buttonsWrapper, saveButton);

		return [title, label, textArea, errorMessage, buttonsWrapper, loadingModalOverlay];
	}

	_createModal() {
		this._modalWrap = Tools.createElement('div', {
			class: 'Modal Modal--default Modal--filterCompetition',
			innerHTML: '<span class="Modal-close"></span>',
		});

		Tools.appendChilds(this._modalWrap, ...this._createContent());

		this._modal = new ModalWindow();

		this._modal.setOnCloseFunc(() => {
			Tools.removeElement(this._modalWrap);
		});

		document.body.appendChild(this._modalWrap);
	}

	/**
	 * Handles save event.
	 * @param {array.<number>} webIds
	 * @param {Element} loadingModalOverlay
	 */
	_handleSave(webIds, loadingModalOverlay) {
		// locking modal with overlay and showing loading spinner
		Tools.replaceClass(loadingModalOverlay, 'hidden', 'visible');

		const urls = document
			.querySelector('textarea[name="excludedUrlsString"]')
			.value.split('\n')
			.filter((url) => url);
		const payload = JSON.stringify({
			excluded_urls: urls,
			web_ids: webIds,
		});

		const handleRemoteErrorMessage = (response) => {
			let parsedResponse = null;

			try {
				parsedResponse = JSON.parse(response);
			} catch (error) {
				return;
			}

			if (parsedResponse && parsedResponse.failed_web_ids) {
				const { failed_web_ids: failedWebIds } = parsedResponse;
				const isSingular = failedWebIds.length === 1;

				const failedWebNames = this.selectedWebs.filter(({ id }) => failedWebIds.includes(id)).map(({ name }) => name);

				/* eslint-disable typescript-eslint/restrict-template-expressions */
				return `Akce byla úspěšně provedena s výjimkou ${
					isSingular ? 'webu' : 'webů'
				}: ${failedWebNames.join(', ')} u ${
					isSingular ? 'kterého' : 'kterých'
				} došlo k přesažení limitu ${SINGLE_WEB_EXCLUDED_URLS_MAX_LENGTH} URL.`;
				/* eslint-enable typescript-eslint/restrict-template-expressions */
			}
		};

		api
			.POST('/app/api/webs/excluded-urls', payload, {
				getErrorMessage: handleRemoteErrorMessage,
				loader: false,
			})
			.then(() => {
				Tools.replaceClass(loadingModalOverlay, 'visible', 'hidden');
				this._modal.close();
			})
			.catch((error) => {
				console.error(error);
				this._modal.close();
			});
	}

	/**
	 * Handles blur event.
	 * @param {string} value
	 * @param {Element} errorMessageElement
	 * @param {Element} saveButton
	 */
	_handleTextAreaBlur(value, errorMessageElement, saveButton) {
		const { isValid, errorMessageText } = this._validateTextAreaValue(value);

		if (isValid) {
			this._hideError(errorMessageElement, saveButton);

			return;
		}

		this._showError(errorMessageElement, errorMessageText, saveButton);
	}

	/**
	 * Handles change event.
	 * @param {string} value
	 * @param {Element} errorMessageElement
	 * @param {Element} saveButton
	 */
	_handleTextAreaChange(value, errorMessageElement, saveButton) {
		if (!this.hasBeenValidated) {
			return;
		}

		const { isValid, errorMessageText } = this._validateTextAreaValue(value);

		if (isValid) {
			this._hideError(errorMessageElement, saveButton);

			return;
		}

		this._showError(errorMessageElement, errorMessageText, saveButton);
	}

	/**
	 * Validates entered urls.
	 * @param {string} value
	 * @return {{isValid: boolean,  errorMessageText?: string}}
	 */
	_validateTextAreaValue(value) {
		this.hasBeenValidated = true;

		const urls = value.split('\n');

		const wrongUrls = urls.filter((url) => url && !isUrlWithProtocol(url));

		const correctUrls = urls.filter((url) => url && isUrlWithProtocol(url));

		if (wrongUrls.length) {
			return {
				errorMessageText: `Špatný formát url: ${wrongUrls.join(', ')}.`,
				isValid: false,
			};
		}

		if (correctUrls.length > WEBS_EXCLUDED_URLS_MAX_LENGTH) {
			return {
				errorMessageText: 'Maximální počet url je 10.',
				isValid: false,
			};
		}

		return {
			isValid: true,
		};
	}

	/**
	 * Displays error message below the textarea and disable savesButton.
	 * @param {Element} errorMessageElement
	 * @param {string} errorMessageText
	 * @param {Element} saveButton
	 */
	_showError(errorMessageElement, errorMessageText, saveButton) {
		saveButton.disabled = true;
		errorMessageElement.innerText = errorMessageText;
		errorMessageElement.classList.remove('hidden');
	}

	/**
	 * Hides error message below and enables savesButton.
	 * @param {Element} errorMessageElement
	 * @param {Element} saveButton
	 */
	_hideError(errorMessageElement, saveButton) {
		saveButton.disabled = false;
		errorMessageElement.classList.add('hidden');
	}
}

export default CompetitionFilterModal;
